import {Component, OnDestroy, OnInit} from '@angular/core';
import {AuthService} from "@auth0/auth0-angular";
import {environment} from "../../environments/environment";
import {getPageInfoDefaults, Page, PageInfo} from "../../models/paginator";
import {AdminPanelService, BulkCreateShipmentsResponse} from "./service/admin-panel.service";
import {Customer} from "../../models/customer";
import {Order, OrderShipmentSummary} from "../../models/order";
import {Subscription} from "rxjs";
import {ActivateEvent, TableColumn} from '../../components/table/table.component'
import {goToUrl} from "../../services/util/util";
import {Link} from "../../models/link";
import {Router} from "@angular/router";
import {Product} from "../../models/product";
import {ShopService} from "../shop/service/shop.service";
import {Discount} from "../../models/discount";
import {BlogService} from "../blog/components/service/blog.service";
import {Blog} from "../../models/blog";
import {Toast} from "tw-elements";
import {ColumnMode} from "@swimlane/ngx-datatable";

/** One table row per order item; the order fields repeat on each row of the same order. */
type OrdersRow = Order & {
    productName: string;
    orderQuantity: number;
    productPrice: number;
    customerFullName: string;
    customerAddress: string;
    shipmentStatusLabel: string;
};

/** Eco statuses in Macedonian for the orders table. */
const SHIPMENT_STATUS_LABELS: { [status: string]: string } = {
    NOT_CREATED: 'Не е креирана',
    PENDING_SUBMISSION: 'Се праќа…',
    SUBMITTED_TO_COURIER: 'Пратена во Eco',
    CREATED: 'Креирана',
    IN_TRANSIT: 'Во транспорт',
    OUT_FOR_DELIVERY: 'Кај курир',
    DELIVERED: 'Доставена',
    FAILED: 'Неуспешна',
    RETURNED: 'Вратена',
    CANCELLED: 'Откажана'
};

@Component({
    selector: 'app-admin-panel',
    templateUrl: './admin-panel.component.html',
    styleUrl: './admin-panel.component.css'
})
export class AdminPanelComponent implements OnInit, OnDestroy {

    protected readonly environment = environment;
    protected readonly ColumnMode = ColumnMode;

    activeTable: string = "orders";

    pageInfo: PageInfo = getPageInfoDefaults();

    customers: Customer[] = [];

    orders: OrdersRow[] = [];

    selectedOrderIds: Set<number> = new Set<number>();

    isBulkCreatingShipments: boolean = false;

    retryingOrderIds: Set<number> = new Set<number>();

    toastMessage: string = '';

    products: Product[] = [];

    discounts: Discount[] = [];

    blogs: Blog[] = [];

    pageNumber: number = 0;

    pageSize: number = 20;

    totalPages: number = 0;

    private ordersPageSub!: Subscription;

    private productsPageSub!: Subscription;

    orderColumns: TableColumn[] = [
        {prop: 'productName', name: 'Производ', width: 310},
        {prop: 'orderQuantity', name: 'Количина', width: 60},
        {prop: 'orderCreated', name: 'Датум', width: 200},
        {prop: 'customerFullName', name: 'Име и презиме', width: 200},
        {prop: 'customerAddress', name: 'Адреса', width: 350},
        {prop: 'customer.phoneNumber', name: 'Број', width: 100},
        {prop: 'id', name: 'Референца', width: 90},
        {prop: 'productPrice', name: 'Цена', width: 100},
        {prop: 'totalPrice', name: 'Вкупна Цена', width: 100},
        {prop: 'usedPoints', name: 'Користени поени за наплата', width: 100},
        {prop: 'usedCoupon', name: 'Искористен купон', width: 100}
    ];

    productColumns: TableColumn[] = [
        {prop: 'name', name: 'Име', width: 200},
        {prop: 'category', name: 'Категорија', width: 80},
        {prop: 'designID', name: 'Дизајн', width: 80},
        {prop: 'description', name: 'Опис', width: 200},
        {prop: 'metaDescription', name: 'Опис', width: 200},
        {prop: 'subCategory', name: 'Подкатегорија', width: 20},
        {prop: 'images', name: 'слики', width: 80},
        {prop: 'altImages', name: 'алт опис на слики', width: 80},
        {prop: 'originalPrice', name: 'Оргинална Цена', width: 60},
        {prop: 'price', name: 'Цена со попуст', width: 60},
        {prop: 'created', name: 'Креиран', width: 100},
        {prop: 'discount', name: 'Попуст', width: 100},
    ];

    customerColumns: TableColumn[] = [
        {prop: 'name', name: 'Име', width: 100},
        {prop: 'surname', name: 'Презиме', width: 100},
        {prop: 'address', name: 'Адреса', width: 100},
        {prop: 'city', name: 'Град', width: 80},
        {prop: 'phoneNumber', name: 'Број', width: 100},
        {prop: 'email', name: 'Е-маил', width: 120},
        {prop: 'couponCode', name: 'Купон код', width: 120},
        {prop: 'totalPoints', name: 'Вкупно поени', width: 120},
        {prop: 'currentPoints', name: 'Неискористени поени', width: 120}
    ];

    discountColumns: TableColumn[] = [
        {prop: 'name', name: 'Име', width: 100},
        {prop: 'discountPercentage', name: 'Процент', width: 100},
        {prop: 'isActive', name: 'Активен', width: 100},
        {prop: 'startDate', name: 'Почетен датум', width: 80},
        {prop: 'endDate', name: 'Краен датум', width: 100},
        {prop: 'discountCode', name: 'Код', width: 120}
    ];

    blogColumns: TableColumn[] = [
        {prop: 'title', name: 'Наслов', width: 100},
        {prop: 'category', name: 'Категорија', width: 100},
        {prop: 'slug', name: 'Слаг', width: 100},
        {prop: 'author.name', name: 'Автор', width: 80},
        {prop: 'summary', name: 'Мета опис', width: 80},
        {prop: 'publishDate', name: 'Датум', width: 80}
    ];

    links: Link[] = [
        {text: "Нарачки", route: "orders"},
        {text: "Потрошувачи", route: "customers"},
        {text: "Продукти", route: "products"},
        {text: "Попусти", route: "discounts"},
        {text: "Бренд амбасадори", route: "ambassadors"},
        {text: "Блог постови", route: "blogs"},
        {text: "Мислења", route: "reviews"}
    ];

    constructor(
        public auth: AuthService,
        private readonly adminPanelService: AdminPanelService,
        protected readonly router: Router,
        private readonly shopService: ShopService,
        private readonly blogService: BlogService) {
    }

    ngOnInit(): void {
        this.getPaginatedOrders();
    }

    ngOnDestroy(): void {
        if (this.ordersPageSub) {
            this.ordersPageSub.unsubscribe();
        }
    }

    private loadData(): void {
        this.getPaginatedOrders();
        this.getPaginatedProducts();
        this.getPaginatedCustomers();
        this.getPaginatedBlogs();
    }

    setActiveTable(table: string) {
        this.activeTable = table;
        switch (table) {
            case "orders":
                this.getPaginatedOrders();
                break;
            case "customers":
                this.getPaginatedCustomers();
                break;
            case "products":
                this.getPaginatedProducts();
                break;
            case "discounts":
                this.getDiscountList();
                break;
            case "ambassadors":
                this.getCustomersWithCouponCode();
                break;
            case "blogs":
                this.getPaginatedBlogs();
                break;
        }
    }

    onPageChange(page: number): void {
        this.pageNumber = page;
        this.loadData();
    }

    getPaginatedOrders(): void {
        this.adminPanelService.getPaginatedOrders(this.pageNumber, this.pageSize);
        this.ordersPageSub?.unsubscribe();
        this.ordersPageSub = this.adminPanelService.ordersPage$.subscribe((page) => {
            this.totalPages = page.totalPages;
            this.orders = this.processOrders(page.content);
            this.pageInfo.totalElements = page.totalElements;
            this.keepOnlyVisibleSelections();
        });
    }

    getPaginatedProducts(): void {
        this.shopService.getAllProducts(this.pageNumber, this.pageSize);
        this.productsPageSub = this.shopService.productsPage$.subscribe((page) => {
            this.totalPages = page.totalPages;
            this.products = page.content;
            this.pageInfo.totalElements = page.totalElements;
        });
    }

    getPaginatedCustomers(): void {
        this.adminPanelService.getAllCustomers(this.pageNumber, this.pageSize);
        this.adminPanelService.customersPage$
            .pipe()
            .subscribe((customersPage: Page<Customer>) => {
                this.customers = customersPage.content;
                this.pageInfo.totalElements = customersPage.totalElements;
            });
    }

    getCustomersWithCouponCode() {
        this.adminPanelService.getAllCustomersWithCouponCode().subscribe((customers) => {
            this.customers = customers;
            this.pageInfo.totalElements = customers.length;
        });
    }

    getDiscountList() {
        this.shopService.getAllDiscounts().subscribe(discounts => {
            this.discounts = discounts
            this.pageInfo.totalElements = discounts.length;
        });
    }

    getPaginatedBlogs() {
        this.blogService.getAllBlogs().subscribe(blogs => {
            this.blogs = blogs
            this.pageInfo.totalElements = blogs.length;
        });
    }

    private processOrders(orders: Order[]): OrdersRow[] {
        return orders.flatMap(order =>
            order.orderItems.map(orderItem => ({
                ...order,
                productName: orderItem?.product?.name ?? '',
                orderQuantity: orderItem?.quantity || 0,
                productPrice: orderItem?.product?.price || 0,
                customerFullName: `${order.customer?.name || ''} ${order.customer?.surname || ''}`,
                customerAddress: `${order.customer?.address || ''} ${order.customer?.city || ''}`,
                shipmentStatusLabel: this.shipmentStatusLabel(order.shipment)
            }))
        );
    }

    createEcoShipments(): void {
        const selectedIds = Array.from(this.selectedOrderIds);
        if (selectedIds.length === 0 || this.isBulkCreatingShipments) {
            return;
        }

        this.isBulkCreatingShipments = true;
        this.adminPanelService.bulkCreateShipments(selectedIds).subscribe({
            next: (response: BulkCreateShipmentsResponse) => {
                this.openToast(this.bulkResultMessage(response));
                this.selectedOrderIds.clear();
                this.getPaginatedOrders();
            },
            error: (error) => {
                this.openToast(error?.error?.message || 'Грешка при креирање на пратки. Обиди се повторно.');
                this.isBulkCreatingShipments = false;
            },
            complete: () => this.isBulkCreatingShipments = false
        });
    }

    retryShipmentForOrder(order: OrdersRow): void {
        const orderId = order.id;
        const shipmentId = order.shipment?.shipmentId ?? order.shipment?.id;
        if (!orderId || !shipmentId || this.retryingOrderIds.has(orderId)) {
            return;
        }

        this.retryingOrderIds.add(orderId);
        this.adminPanelService.retryShipment(shipmentId).subscribe({
            next: (result) => {
                this.openToast(result.errorMessage
                    ? `Нарачка #${orderId}: ${result.errorMessage}`
                    : `Нарачка #${orderId} е повторно пратена во Eco.`);
                this.getPaginatedOrders();
            },
            error: () => {
                this.openToast(`Грешка при повторно праќање на нарачка #${orderId}.`);
                this.retryingOrderIds.delete(orderId);
            },
            complete: () => this.retryingOrderIds.delete(orderId)
        });
    }

    toggleOrderSelection(order: OrdersRow, selected: boolean): void {
        if (!order.id || !this.isOrderEligibleForShipment(order)) {
            return;
        }
        if (selected) {
            this.selectedOrderIds.add(order.id);
        } else {
            this.selectedOrderIds.delete(order.id);
        }
    }

    toggleSelectAllEligible(selected: boolean): void {
        for (const order of this.visibleEligibleOrders) {
            if (selected) {
                this.selectedOrderIds.add(order.id!);
            } else {
                this.selectedOrderIds.delete(order.id!);
            }
        }
    }

    isOrderSelected(orderId?: number): boolean {
        return !!orderId && this.selectedOrderIds.has(orderId);
    }

    get visibleEligibleOrders(): OrdersRow[] {
        return this.uniqueOrders().filter(order => this.isOrderEligibleForShipment(order));
    }

    get allVisibleEligibleSelected(): boolean {
        const eligible = this.visibleEligibleOrders;
        return eligible.length > 0 && eligible.every(order => this.selectedOrderIds.has(order.id!));
    }

    /** No shipment yet, or the last attempt failed - and the order is still open. */
    isOrderEligibleForShipment(order: OrdersRow): boolean {
        const orderStatus = order.status?.toUpperCase();
        if (orderStatus === 'CANCELLED' || orderStatus === 'DELIVERED') {
            return false;
        }
        const status = order.shipment?.status?.toUpperCase();
        return !status || status === 'NOT_CREATED' || status === 'FAILED';
    }

    shouldShowRetry(order: OrdersRow): boolean {
        return order.shipment?.status?.toUpperCase() === 'FAILED' && !!(order.shipment?.shipmentId ?? order.shipment?.id);
    }

    openTracking(trackingUrl?: string): void {
        if (trackingUrl) {
            window.open(trackingUrl, '_blank');
        }
    }

    copyTrackingLink(trackingUrl?: string): void {
        if (!trackingUrl) {
            return;
        }
        navigator.clipboard.writeText(trackingUrl)
            .then(() => this.openToast('Линкот за следење е копиран.'))
            .catch(() => this.openToast('Линкот не можеше да се копира.'));
    }

    private shipmentStatusLabel(shipment?: OrderShipmentSummary | null): string {
        const status = shipment?.status?.toUpperCase() ?? 'NOT_CREATED';
        return SHIPMENT_STATUS_LABELS[status] ?? status;
    }

    private bulkResultMessage(response: BulkCreateShipmentsResponse): string {
        const errors = response.shipments
            .filter(shipment => shipment.errorMessage)
            .map(shipment => `#${shipment.orderId}: ${shipment.errorMessage}`);
        const summary = `Пратени во Eco: ${response.successful}. Неуспешни: ${response.failed}.`;
        return errors.length ? `${summary} ${errors.join(' ')}` : summary;
    }

    private keepOnlyVisibleSelections(): void {
        const visibleIds = new Set(this.uniqueOrders().map(order => order.id));
        for (const selectedId of Array.from(this.selectedOrderIds)) {
            if (!visibleIds.has(selectedId)) {
                this.selectedOrderIds.delete(selectedId);
            }
        }
    }

    private uniqueOrders(): OrdersRow[] {
        const byId = new Map<number, OrdersRow>();
        for (const order of this.orders) {
            if (order.id && !byId.has(order.id)) {
                byId.set(order.id, order);
            }
        }
        return Array.from(byId.values());
    }

    private openToast(message: string): void {
        this.toastMessage = message;
        setTimeout(() => new Toast(document.getElementById("toast")).show());
    }

    onDiscountRowClick(event: ActivateEvent) {
        goToUrl(this.router, 'shop/product/discount/' + event.row?.id + '/edit')
    }

    onProductRowClick(event: ActivateEvent) {
        goToUrl(this.router, 'shop/product/' + event.row?.id + '/edit')
    }

    onBlogRowClick(event: ActivateEvent) {
        goToUrl(this.router, 'blogs/' + event.row?.id + '/edit')
    }

    protected readonly goToUrl = goToUrl;
}